import React from 'react';
import { Drawer, Box, Typography, IconButton, useTheme, Button } from '@mui/material';
import { X, Users } from 'lucide-react';
import { Service } from '../../types/service';
import ServiceForm from './ServiceForm';
import ResourceMappingSection from './ResourceMappingSection';
import ServiceAddonMappingSection from './ServiceAddonMappingSection';

interface ServiceDrawerProps {
  open: boolean;
  onClose: () => void;
  service?: Service;
  refetchServices: () => void;
}

const ServiceDrawer: React.FC<ServiceDrawerProps> = ({
  open,
  onClose,
  service,
  refetchServices,
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = React.useState<'details' | 'resources' | 'addons'>('details');

  // Reset tab when drawer opens/closes
  React.useEffect(() => {
    if (!open) {
      setActiveTab('details');
    }
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600 },
          p: 0,
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {service ? 'Edit Service' : 'Add Service'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <X size={18} />
        </IconButton>
      </Box>

      {service && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            gap: 1,
          }}
        >
          <Button
            variant={activeTab === 'details' ? 'contained' : 'text'}
            onClick={() => setActiveTab('details')}
            sx={{ textTransform: 'none' }}
          >
            Service Details
          </Button>
          <Button
            variant={activeTab === 'resources' ? 'contained' : 'text'}
            startIcon={<Users size={16} />}
            onClick={() => setActiveTab('resources')}
            sx={{ textTransform: 'none' }}
          >
            Manage Resources
          </Button>
          {!service.isAddonService && (
            <Button
              variant={activeTab === 'addons' ? 'contained' : 'text'}
              startIcon={<Users size={16} />}
              onClick={() => setActiveTab('addons')}
              sx={{ textTransform: 'none' }}
            >
              Manage Addons
            </Button>
          )}
        </Box>
      )}

      <Box sx={{ height: '100%', overflow: 'auto' }}>
        {!service || activeTab === 'details' ? (
          <ServiceForm service={service} onClose={onClose} />
        ) : service && activeTab === 'addons' ? (
          <ServiceAddonMappingSection
            serviceId={service.id}
            existingMappings={service.serviceAddons}
          />
        ) : (
          service &&
          activeTab === 'resources' && (
            <ResourceMappingSection
              serviceId={service.id}
              existingMappings={service.serviceResourceMapping}
              onMappingsChange={refetchServices}
            />
          )
        )}
      </Box>
    </Drawer>
  );
};

export default ServiceDrawer;
